import type { SiteHealthFindingRow, SiteHealthSideReads } from "@/types/tina";

const DAY_MS = 86_400_000;
const athensDay = new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Athens" });

/** The Europe/Athens calendar day, the same clock as "new today" — not the UTC date. */
function day(iso: string | null | undefined): string {
  const ms = iso ? Date.parse(iso) : Number.NaN;
  return Number.isNaN(ms) ? "an unknown day" : athensDay.format(new Date(ms));
}

/** A number from the evidence, or "?" when tina did not send it — never "undefined". */
function num(value: number | null | undefined): string {
  return value == null || Number.isNaN(value) ? "?" : String(value);
}

/** "1 day", "2 days", "? days". */
export function plural(n: number | null | undefined, word: string): string {
  return `${num(n)} ${word}${n === 1 ? "" : "s"}`;
}

function pct(share: number | null | undefined): string {
  return share == null || Number.isNaN(share) ? "?%" : `${Math.round(share * 100)}%`;
}

function rounded(value: number | null | undefined): string {
  return value == null || Number.isNaN(value) ? "?" : String(Math.round(value));
}

function sinceClause(iso: string | null | undefined): string {
  return iso ? ` (since ${day(iso)})` : "";
}

const BATCH_NOTE = " This is an Instagram/Facebook batch run: every account in the batch is affected.";

function reasons(list: string[] | undefined): string {
  if (!list || list.length === 0) return "";
  return ` Top reason${list.length > 1 ? "s" : ""}: ${list.join("; ")}.`;
}

function side(name: string, reads: SiteHealthSideReads | null | undefined): string | null {
  if (!reads || !reads.crossed) return null;
  return `${name}: ${num(reads.unreadable)} of ${num(reads.tried)} pages could not be read in 3 days (${pct(reads.share)}).${reasons(reads.top_reasons)}`;
}

/** `cluttered`: one clause per signal that fired, so a card never reports a 0% it did not flag. */
function clutterSentence(e: SiteHealthFindingRow["evidence"]): string {
  const parts: string[] = [];
  if ((e.boilerplate_share ?? 0) > 0) {
    parts.push(`${pct(e.boilerplate_share)} of each page is template text repeated on most pages (about ${num(e.boilerplate_chars)} chars)`);
  }
  if ((e.sibling_page_share ?? 0) > 0) {
    const heading = e.block_heading ? ` under "${e.block_heading}"` : "";
    parts.push(`${pct(e.sibling_page_share)} of pages list 3 or more other events of this site${heading}`);
  }
  const found = parts.length > 0 ? parts.join("; ") : "pages carry boilerplate or other events";
  return `In ${plural(e.pages, "page")} over 14 days, ${found}. Extract reads all of it.`;
}

/** The one plain sentence a card shows (tina spec §10.1). */
export function describeFinding(f: SiteHealthFindingRow): string {
  const e = f.evidence;
  switch (f.check_name) {
    case "discover_failing":
      return `The last ${plural(e.streak, "run")} failed${sinceClause(e.since)}. Last error: ${e.last_error_type ?? "unknown"}${e.last_error_message ? ` — ${e.last_error_message}` : ""}. It last found ${plural(e.last_found_count, "link")} on ${day(e.last_found_at)}.${e.batched ? BATCH_NOTE : ""}`;
    case "discover_empty":
      return `The last ${plural(e.streak, "run")} found 0 links${sinceClause(e.since)}. It last found ${plural(e.last_found_count, "link")} on ${day(e.last_found_at)}.`;
    case "pages_unreadable":
      return (
        [side("Scrape", e.scrape), side("Refresh", e.refresh)].filter(Boolean).join(" ") ||
        "Most pages could not be read in the last 3 days."
      );
    case "new_links_fade":
      return `${num(e.recent_new)} new links in the last 14 days, against a normal ${rounded(e.normal_new)} (${pct(e.ratio)}).`;
    case "social_quiet": {
      // Ruling R21: Instagram itself confirmed the account is gone, so that leads the sentence.
      const gone = e.profile_confirmed_gone ? "Instagram confirms the account is gone. " : "";
      const quiet = e.last_event_at
        ? `No event for ${plural(e.days_quiet, "day")} (last on ${day(e.last_event_at)}).`
        : `No event since it was added, ${plural(e.days_quiet, "day")} ago.`;
      const unavailable =
        e.profile_unavailable && !e.profile_confirmed_gone ? " Instagram says the account is unavailable." : "";
      return `${gone}${quiet}${unavailable}`;
    }
    case "mostly_junk":
      return `This week ${pct(e.this_week?.useful_share)} of ${num(e.this_week?.n)} pages were useful and ${pct(e.this_week?.junk_share)} were junk; last week ${pct(e.last_week?.useful_share)} useful.${reasons(e.top_reasons)}`;
    case "link_count_drop":
      // The last 3 FOUND seeds (tina's DROP_RECENT_RUNS), not the last 3 runs.
      return `The last 3 successful runs found ${e.recent_counts?.length ? e.recent_counts.join(", ") : "?"} links. Normal is about ${rounded(e.prior_median)}.`;
    case "selector_empty":
      return `${num(e.no_rows)} of ${num(e.n)} pages in 14 days ended with "selector found no rows" (${pct(e.share)}).`;
    case "images_lost":
      return `${num(e.lost)} of ${num(e.tried)} pages in 3 days had a poster we could not download (${pct(e.share)}). Their events have no image.`;
    case "cluttered":
      return clutterSentence(e);
    case "tiers_missing": {
      // A yield check: zero tiers looks the same as "every event is free", so name what the
      // reads should have carried and send the curator to the adapter.
      const via = e.adapters?.length ? ` via ${e.adapters.join(", ")}` : "";
      return `The last ${plural(e.events, "event read")}${via} returned no ticket tiers. The source or its endpoint has probably changed.`;
    }
    default:
      return `New check from tina: ${String(f.check_name)}.`;
  }
}

/** "Broken since": the evidence's own `since` when it has one, else when tina first saw it. */
export function brokenSince(f: SiteHealthFindingRow): string {
  return f.evidence.since ?? f.first_seen_at;
}

export function brokenDays(f: SiteHealthFindingRow, now: Date): number {
  return Math.max(0, Math.floor((now.getTime() - Date.parse(brokenSince(f))) / DAY_MS));
}

export function isNewToday(f: SiteHealthFindingRow, now: Date): boolean {
  return athensDay.format(new Date(f.first_seen_at)) === athensDay.format(now);
}

/** Up to five example events to open, from whichever evidence field the check filled. */
export function sampleEventIds(f: SiteHealthFindingRow): string[] {
  const e = f.evidence;
  return [
    ...(e.sample_event_ids ?? []),
    ...(e.scrape?.sample_event_ids ?? []),
    ...(e.refresh?.sample_event_ids ?? []),
  ].slice(0, 5);
}
